import React from 'react';
import { Phone, ChevronRight, MapPin, Store } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, stats, images } from '../data/mock';
import AnimatedStat from './AnimatedStat';

const HeroSection = () => {
  return (
    <section id="accueil" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-cyan-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-cyan/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-blue/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary-blue/5 to-primary-cyan/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-blue/10 dark:bg-primary-blue/20 rounded-full border border-primary-blue/20">
              <Store className="w-4 h-4 text-primary-blue dark:text-primary-cyan" />
              <span className="text-primary-blue dark:text-primary-cyan font-medium text-sm">Boutique ouverte à Corbigny</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
              Votre boutique
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-primary-cyan">
                informatique
              </span>
              de proximité
            </h1>

            {/* Description */}
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
              Vente d'ordinateurs, smartphones, tablettes et caméras de surveillance. Réparation PC, installation à domicile et <strong className="text-primary-blue dark:text-primary-cyan">assistance gratuite à vie</strong> sur nos produits.
            </p>

            {/* Location Info */}
            <a 
              href={companyInfo.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:text-primary-blue dark:hover:text-primary-cyan transition-colors group"
            >
              <div className="w-10 h-10 bg-primary-cyan/10 rounded-full flex items-center justify-center group-hover:bg-primary-cyan/20 transition-colors">
                <MapPin className="w-5 h-5 text-primary-cyan" />
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Adresse</span>
                <p className="font-medium">6 rue des Tépins, 58800 Corbigny</p>
              </div>
            </a>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a href={companyInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-primary-blue hover:bg-primary-blue/90 text-white px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex items-center gap-3">
                  <MapPin className="w-5 h-5" />
                  Venir en boutique
                </Button>
              </a>
              <a href={`tel:${companyInfo.phone.replace(/\./g, '')}`}>
                <Button variant="outline" className="border-2 border-primary-blue dark:border-primary-cyan text-primary-blue dark:text-primary-cyan hover:bg-primary-blue hover:text-white px-8 py-6 text-lg rounded-full transition-all duration-300 flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  {companyInfo.phone}
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - Image with floating cards */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative z-10">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={images.hero}
                  alt="Boutique informatique CLIC58 à Corbigny"
                  className="w-full h-[500px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-blue/40 via-transparent to-transparent" />
              </div>

              {/* Floating Card - Assistance gratuite */}
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-5 border border-gray-100 dark:border-gray-700 animate-float">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-cyan to-primary-blue rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🎧</span>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">Assistance</p>
                    <p className="text-primary-cyan font-semibold">Gratuite à vie !</p>
                  </div>
                </div>
              </div>

              {/* Floating Card - Boutique */}
              <div className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-5 border border-gray-100 dark:border-gray-700 animate-float-delayed">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-blue to-primary-cyan rounded-xl flex items-center justify-center">
                    <Store className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">Boutique</p>
                    <p className="text-gray-500 dark:text-gray-400">Ouverte à Corbigny</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-10 right-10 w-20 h-20 border-2 border-primary-cyan/30 rounded-full" />
            <div className="absolute bottom-20 left-10 w-12 h-12 bg-primary-blue/20 rounded-lg rotate-45" />
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <AnimatedStat key={index} value={stat.value} label={stat.label} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
